/**
 * Danh mục ngành của tiệm — MỘT nguồn cho DB (ENUM `business_type`, test `nganh.int` so `enum_range`), máy chủ (zod mở tiệm,
 * chu kỳ quay lại mặc định), web quầy (chip ngành ở form mở tiệm) và Mini App (màu + biểu tượng). ADR-082.
 *
 * SAPA là nền tảng đặt lịch cho cửa hàng dịch vụ theo giờ hẹn; làm đẹp chỉ là một nhóm. Danh mục không thêm ngành cần quản
 * lý sân / phòng / giường (NOT-DOING). Ghi chú sự thật (không phải luật; luật "trung tính ngành" đã bỏ 26/10, ADR-117): Zalo
 * từng đòi giấy phép với một số ngành (docs/zalo/kiem-duyet-da-nganh.md) — rủi ro duyệt Mini App do chủ dự án tự quyết. `xe` chỉ là rửa / vệ sinh / đánh bóng, ẩn khỏi form tự đăng
 * ký (`moTuDangKy: false`) tới khi Zalo xác nhận nhóm của rửa xe — người vận hành vẫn mở được bằng `db:tao-tiem`.
 *
 * Tệp chạy được trong trình duyệt (Mini App dựng es2015): không import gì, không bigint. Tên ngành (`TEN_NGANH`) để ở binding
 * riêng: Mini App không import nó nên chữ "Spa" / "Massage" không lọt vào gói của app khách.
 */

/** Thứ tự = thứ tự giá trị ENUM `business_type` (0000, 0068, 0069). Thêm ngành: migration `ADD VALUE … BEFORE 'khac'` + mục ở đây. */
export const NGANH_TIEM = [
  'hair',
  'nail',
  'spa',
  'massage',
  'thu-cung',
  'studio',
  'xe',
  'khac',
] as const
export type NganhTiem = (typeof NGANH_TIEM)[number]

export type NhomNganh = 'dich-vu' | 'lam-dep'

export type BieuTuongNganh =
  | 'keo'
  | 'lo-son'
  | 'la-sen'
  | 'giot-nuoc'
  | 'ban-chan'
  | 'may-anh'
  | 'o-to'
  | 'to-lich'

/**
 * Bộ màu của một ngành (Mini App + web quầy từ v0.23.0): chữ trắng trên `primary` / `press` và `primary` trên `soft` /
 * nền trắng / nền web quầy đạt ≥ 4.5:1; hai ngành bất kỳ cách nhau ΔE OKLab ≥ 0,10 và mỗi `primary` cách mọi màu trạng
 * thái của quầy ≥ 0,09 (test `nganh.test.ts`, `apps/admin/src/mau-trang-thai.test.ts`). `line` = viền / chip nhạt (chỉ
 * web quầy dùng). Sửa một màu = sửa một dòng ở đây; hair và khac giữ nguyên từng mã (khac = nhận diện SAPA).
 */
export interface MauNganh {
  primary: string
  press: string
  soft: string
  line: string
  bia1: string
  bia2: string
}

export interface ThongTinNganh {
  nhom: NhomNganh
  mau: MauNganh
  bieuTuong: BieuTuongNganh
  /** Chu kỳ "nhắc khách quay lại" mặc định, ngày (CHECK 7..180 của 0039). */
  quayLaiNgay: number
  /** Hiện ở form tự mở tiệm. */
  moTuDangKy: boolean
}

export const NGANH: Record<NganhTiem, ThongTinNganh> = {
  hair: {
    nhom: 'lam-dep',
    mau: {
      primary: '#1e3a5f',
      press: '#152a45',
      soft: '#e6edf6',
      line: '#c5d4e9',
      bia1: '#152a45',
      bia2: '#3d6694',
    },
    bieuTuong: 'keo',
    quayLaiNgay: 35,
    moTuDangKy: true,
  },
  nail: {
    nhom: 'lam-dep',
    mau: {
      primary: '#a12996',
      press: '#830a7a',
      soft: '#fde9f9',
      line: '#ebc5e4',
      bia1: '#830a7a',
      bia2: '#c764bb',
    },
    bieuTuong: 'lo-son',
    quayLaiNgay: 21,
    moTuDangKy: true,
  },
  spa: {
    nhom: 'lam-dep',
    mau: {
      primary: '#613d7e',
      press: '#482762',
      soft: '#f5ecfd',
      line: '#dccbec',
      bia1: '#482762',
      bia2: '#8768a2',
    },
    bieuTuong: 'la-sen',
    quayLaiNgay: 28,
    moTuDangKy: true,
  },
  massage: {
    nhom: 'lam-dep',
    mau: {
      primary: '#3c5b11',
      press: '#274300',
      soft: '#ebf4e3',
      line: '#c8d9b9',
      bia1: '#274300',
      bia2: '#668248',
    },
    bieuTuong: 'giot-nuoc',
    quayLaiNgay: 14,
    moTuDangKy: true,
  },
  'thu-cung': {
    nhom: 'dich-vu',
    mau: {
      primary: '#816512',
      press: '#674d00',
      soft: '#f6f0e0',
      line: '#dfd2b2',
      bia1: '#674d00',
      bia2: '#a88f53',
    },
    bieuTuong: 'ban-chan',
    quayLaiNgay: 30,
    moTuDangKy: true,
  },
  studio: {
    nhom: 'dich-vu',
    mau: {
      primary: '#362ca1',
      press: '#250f81',
      soft: '#ebefff',
      line: '#c9d0fa',
      bia1: '#250f81',
      bia2: '#5a5ec2',
    },
    bieuTuong: 'may-anh',
    quayLaiNgay: 90,
    moTuDangKy: true,
  },
  xe: {
    nhom: 'dich-vu',
    mau: {
      primary: '#4a3b13',
      press: '#332500',
      soft: '#f4f0e6',
      line: '#dad2bf',
      bia1: '#332500',
      bia2: '#6f6241',
    },
    bieuTuong: 'o-to',
    quayLaiNgay: 30,
    moTuDangKy: false,
  },
  khac: {
    nhom: 'dich-vu',
    mau: {
      primary: '#2f5fa7',
      press: '#234a85',
      soft: '#e8eef8',
      line: '#c3d3ec',
      bia1: '#234a85',
      bia2: '#4f80c4',
    },
    bieuTuong: 'to-lich',
    quayLaiNgay: 28,
    moTuDangKy: true,
  },
}

/** Giao diện khi CHƯA biết ngành (màn nhập mã, deep-link bằng uuid, ngành lạ): trung tính — không phải màu spa. */
export const GIAO_DIEN_MAC_DINH: { mau: MauNganh; bieuTuong: BieuTuongNganh } = {
  mau: NGANH.khac.mau,
  bieuTuong: NGANH.khac.bieuTuong,
}

export function laNganhTiem(x: unknown): x is NganhTiem {
  return typeof x === 'string' && (NGANH_TIEM as readonly string[]).includes(x)
}

/**
 * Ngành để chủ tiệm CHỌN (form mở tiệm, Cấu hình → Thông tin tiệm): nhóm "dịch vụ" trước, làm đẹp sau, `khac` luôn cuối;
 * chỉ ngành `moTuDangKy`, cộng `giu` (ngành tiệm đang dùng dù đang ẩn — không để chủ tiệm mất lựa chọn của mình).
 */
export function nganhDeChon(giu?: NganhTiem): NganhTiem[] {
  const hien = (n: NganhTiem) => NGANH[n].moTuDangKy || n === giu
  return [
    ...NGANH_TIEM.filter((n) => n !== 'khac' && NGANH[n].nhom === 'dich-vu' && hien(n)),
    ...NGANH_TIEM.filter((n) => NGANH[n].nhom === 'lam-dep' && hien(n)),
    'khac',
  ]
}

/** Tên hiển thị ở web quầy (form mở tiệm). Mini App KHÔNG dùng. */
export const TEN_NGANH: Record<NganhTiem, string> = {
  hair: 'Tóc',
  nail: 'Nail – mi',
  spa: 'Spa',
  massage: 'Massage',
  'thu-cung': 'Thú cưng',
  studio: 'Studio ảnh',
  xe: 'Chăm sóc xe',
  khac: 'Dịch vụ khác',
}
